import { useAppState, useUiState, useUserState } from '@/core/state/react'
import { uiActions } from '@/core/state/ui'
import { decideFeature } from '@/core/state/features'
import { appSelectors } from '@/core/state/app'
import { runOptimization } from '@/core/commands/optimization'
import { runSimulation } from '@/core/commands/simulation'
import './toolbar.css'

/**
 * The map toolbar (Phase 1F), replacing `views/components/MapViewToolbar.vue`.
 *
 * It reads the two facts it needs from the stores rather than from props:
 * whether a run is in flight (`optimizing` / `simulating`) and whether this
 * tier may start one. Both were Vue-only until this sub-plan — the simulation
 * flag was a bare `ref(false)` inside a composable, and the gate made fresh
 * upgrade-modal refs for every caller.
 *
 * It is rendered ONCE. `MapView.vue` declared it twice, in two mutually
 * exclusive branches, and the stat bar is `v-show` rather than `v-if` — so with
 * the summary panel off, two `toolbar-open-menu` testids existed at the same
 * time. That is the testid the shared `openMenu()` fixture clicks from ~28
 * places, and a strict-mode violation would have taken most of the E2E suite
 * with it. No test turns the panel off, so it never fired.
 */

/**
 * The tooltip copy, recovered verbatim from `MapViewToolbar.vue`'s four
 * `v-tooltip`s. Two of them said something different when the feature was
 * locked — that is where the upgrade prompt lived, so dropping them would have
 * removed the only place the app explains what the padlock is for.
 *
 * Help had no tooltip in the Vue version and has none here.
 */
type Tip = { title: string; lines: string[] }

const TIPS: Record<string, { open: Tip; locked?: Tip }> = {
  'toolbar-run-simulation': {
    open: {
      title: '🎲 Monte Carlo Risk Analysis',
      lines: [
        'Test hundreds of scenarios to understand risk ranges',
        '💡 Enterprise Feature - Model uncertainty before it happens',
      ],
    },
    locked: {
      title: '🎲 Monte Carlo Risk Analysis',
      lines: [
        'Available in the full trial—unlock uncertainty analysis.',
        '💡 Upgrade to model risk and service variability',
      ],
    },
  },
  'toolbar-run-optimization': {
    open: {
      title: '🚀 Optimize Your Network',
      lines: [
        'Find the lowest-cost configuration in 60 seconds',
        '💡 Analysis that takes consultants months, done instantly',
      ],
    },
    locked: {
      title: '🔒 Upgrade to Run Custom Scenarios',
      lines: ['Optimize your network in 60 seconds', '💰 Typical savings: 15-30% logistics cost'],
    },
  },
  'toolbar-open-menu': {
    open: {
      title: '🎯 Access All Features',
      lines: [
        'Generate reports • Import data • Configure scenarios',
        '🔨 Test greenfield locations • Customize visualization',
      ],
    },
  },
}

type ButtonProps = {
  testId: string
  tour?: string
  icon: string
  label: string
  busy?: boolean
  locked?: boolean
  blocked?: boolean
  onClick: () => void
}

function ToolbarButton({ testId, tour, icon, label, busy, locked, blocked, onClick }: ButtonProps) {
  const tip = TIPS[testId] && (locked ? TIPS[testId].locked ?? TIPS[testId].open : TIPS[testId].open)
  const tipId = tip ? `${testId}-tip` : undefined

  return (
    <span className="toolbar-tip-host">
    <button
      type="button"
      data-testid={testId}
      data-tour={tour}
      className="toolbar-btn"
      disabled={Boolean(busy) || Boolean(locked) || Boolean(blocked)}
      aria-busy={busy ? 'true' : undefined}
      // A padlock is a glyph, and a screen reader does not read glyphs. Without
      // this the button announces "Run Optimization, dimmed" and nothing says
      // the tier is why.
      aria-label={locked ? `${label} — locked, upgrade to unlock` : undefined}
      // A disabled button is not hoverable in every browser and never
      // focusable, so the tooltip is described on the host below rather than
      // here — but the association is still announced when it can be read.
      aria-describedby={tipId}
      onClick={onClick}
    >
      <span className="toolbar-btn__content">
        <i className={`mdi ${icon} toolbar-icon`} aria-hidden="true" />
        {locked && <i className="mdi mdi-lock toolbar-lock" aria-hidden="true" data-testid={`${testId}-lock`} />}
        {label}
      </span>
      {busy && <span className="toolbar-spinner" aria-hidden="true" />}
    </button>
      {tip && (
        <span className="toolbar-tip" id={tipId} role="tooltip">
          <span className="toolbar-tip__title">{tip.title}</span>
          {tip.lines.map((line) => (
            <span key={line} className="toolbar-tip__line">
              {line}
            </span>
          ))}
        </span>
      )}
    </span>
  )
}

export function Toolbar() {
  const optimizing = useUiState((s) => s.optimizing)
  const simulating = useUiState((s) => s.simulating)
  const currentScenario = useAppState((s) => s.currentScenario)

  // SUBSCRIBED, not read. `hasFeature()` reads the stores directly, so calling
  // it here would compute the right answer once and never re-render: a session
  // whose tier and grants arrive from /api/auth/features after mount would keep
  // both buttons padlocked for the rest of the page's life.
  const isDemo = useAppState(appSelectors.isDemoExperience)
  const subscriptionTier = useAppState((s) => s.subscriptionTier)
  const features = useUserState((s) => s.features)

  const gate = { isDemo, subscriptionTier, features }
  const canOptimize = decideFeature('run_optimization', gate)
  const canSimulate = decideFeature('run_simulation', gate)

  // Vue's rule, kept exactly: an optimization in flight blocks Run Simulation,
  // but a simulation in flight does NOT block Run Optimization — both Vue
  // buttons were `:disabled="isOptimizing || !canRun…"`. The first version of
  // this port blocked both on either run, which was a behaviour change nobody
  // decided on. Only a tier restriction counts as locked: a padlock on a button
  // the user may press in a moment would tell them to buy something they have.

  return (
    <div className="scvc-toolbar" data-testid="toolbar">
      <ToolbarButton
        testId="toolbar-run-simulation"
        icon="mdi-chart-bell-curve"
        label="Run Simulation"
        busy={simulating}
        locked={!canSimulate}
        blocked={optimizing}
        onClick={() => void runSimulation()}
      />
      <ToolbarButton
        testId="toolbar-run-optimization"
        tour="run-optimization"
        icon="mdi-rocket-launch"
        label="Run Optimization"
        busy={optimizing}
        locked={!canOptimize}
        onClick={() => void runOptimization()}
      />

      <span className="toolbar-spacer" />
      <div className="scenario-name">
        <span className="scenario-name-text">{currentScenario}</span>
      </div>
      <span className="toolbar-spacer" />

      {/* Help and Menu stay usable while a run is in flight: the way out of a
          long optimization must not be blocked by the optimization. */}
      <ToolbarButton
        testId="toolbar-open-help"
        icon="mdi-help-circle-outline"
        label="Help"
        onClick={() => uiActions.openHelpDialog()}
      />
      <ToolbarButton
        testId="toolbar-open-menu"
        icon="mdi-menu"
        label="Menu"
        onClick={() => uiActions.openMenuPanel()}
      />
    </div>
  )
}
